import { Button, Divider, Modal, Spin } from "antd";
import type { ModalProps } from "antd";

import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { execute } from "../../../../utils/helpers/execute";

interface IProps<T> {
  id: string;
  title: string;
  hideModal: () => void;
  defaultValues?: T;
  children?: React.ReactNode;
  onSubmit: (data: any) => void;
  confirmButtonText: string;
  loading: boolean;
  danger?: boolean;
  maskClosable?: boolean;
  style?: React.CSSProperties;
  width?: string | number;
  /** Modal width follows content (capped by viewport). Useful for tables with variable columns. */
  fitContent?: boolean;
  /** When false, modal body does not scroll; use internal scroll on a Table (scroll.y) instead. */
  bodyScroll?: boolean;
  /** Hide Cancel/Confirm footer buttons (dialog is closed via the X). */
  hideFooter?: boolean;
  bodyStyle?: React.CSSProperties;
  modalStyles?: ModalProps["styles"];
  confirmClick?: () => Promise<void> | void;
}

function FormHookModal<T>(props: IProps<T>) {
  const {
    id,
    title,
    hideModal,
    defaultValues,
    children,
    confirmButtonText,
    loading,
    danger,
    maskClosable = true,
    style,
    width,
    fitContent = false,
    bodyScroll = true,
    hideFooter = false,
    bodyStyle: bodyStyleProp,
    modalStyles,
  } = props;

  const { t } = useTranslation();

  const methods = useForm<any>({
    defaultValues: { ...defaultValues },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  const onSubmit = async (data: any) => {
    await execute({
      callback: async () => {
        await props.onSubmit(data);
        hideModal();
      },
      fallback: (error) => {},
      finallyCallback: () => {},
      throwException: false,
    });
  };

  const handleConfirmClick = async () => {
    if (props.confirmClick) {
      await execute({
        callback: async () => {
          await props.confirmClick!();
        },
        fallback: (error) => {},
        finallyCallback: () => {},
        throwException: false,
      });
    } else {
      handleSubmit(onSubmit)();
    }
  };

  const resolvedWidth = fitContent ? "max-content" : width ?? "50vw";
  const mergedModalStyle: React.CSSProperties = {
    top: "20px",
    ...(fitContent ? { maxWidth: "min(95vw, 1400px)" } : {}),
    ...(style || {}),
  };
  const mergedBodyStyle: React.CSSProperties = bodyScroll
    ? {
        maxHeight: "75vh",
        overflowY: "auto",
        overflowX: fitContent ? "auto" : "hidden",
        ...bodyStyleProp,
      }
    : {
        maxHeight: "90vh",
        overflow: "hidden",
        overflowX: "hidden",
        ...bodyStyleProp,
      };
  const mergedModalStyles: ModalProps["styles"] = {
    ...(fitContent
      ? {
          content: {
            width: "max-content",
            maxWidth: "min(95vw, 1400px)",
            minWidth: 360,
            ...(!bodyScroll ? { maxHeight: "90vh", overflow: "hidden" } : {}),
          },
        }
      : {}),
    ...modalStyles,
  };

  return (
    <FormProvider {...methods}>
      <form id={id} onSubmit={handleSubmit(onSubmit)}>
        <Modal
          key={id}
          title={title}
          open
          closable
          maskClosable={maskClosable}
          destroyOnClose
          onCancel={() => hideModal()}
          afterClose={() => hideModal()}
          width={resolvedWidth}
          style={mergedModalStyle}
          styles={mergedModalStyles}
          bodyStyle={mergedBodyStyle}
          footer={
            hideFooter ? null : (
              <>
                <Divider style={{ margin: "10px" }} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    onClick={() => hideModal()}
                    style={{ marginInlineEnd: 8 }}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    onClick={handleConfirmClick}
                    type="primary"
                    loading={loading}
                    danger={danger}
                  >
                    {confirmButtonText}
                  </Button>
                </div>
              </>
            )
          }
        >
          <Divider style={{ margin: "8px 0 8px 0" }} />
          <Spin spinning={loading}>{children}</Spin>
        </Modal>
      </form>
    </FormProvider>
  );
}

export default FormHookModal;
